import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {AuthService} from '../../../../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterService} from '../../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {CommissionSettlementService} from '../../../../shared/services/commission-settlement.service';
import {CommissionSettlement} from '../../../../shared/interface/commission-settlement';
import moment from "moment";
import {IMPORT_STATUS_IN_PROGRESS, COMMISSION_SETTLEMENT_STATUS_TO_GENERATE} from "../../../../shared/services/dict.service";

@Component({
  selector: 'app-all-settlements-list',
  templateUrl: './all-settlements-list.component.html',
  styleUrls: ['./all-settlements-list.component.scss']
})
export class AllSettlementsListComponent implements OnDestroy, AfterViewInit {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  readonly COMMISSION_SETTLEMENT_STATUS_TO_GENERATE = COMMISSION_SETTLEMENT_STATUS_TO_GENERATE;

  initialFilters: any;
  items: CommissionSettlement[] = [];
  totalData = 0;
  displayedColumns: string[] = [
    'number', 'billingMonth', 'party', 'contractOwner', 'status',
    'totalCollection', 'totalCommission', 'assetsRegional',
  ];
  userPermissions: ReplaySubject<UserPermissions>;
  processingRequest = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private commissionSettlementService: CommissionSettlementService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.commissionSettlementService.commissionSettlementListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CommissionSettlement>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();

    // const date = moment();
    // date.startOf('month');
    // date.subtract(1, 'month');
    //
    // this.initialFilters.billingMonth = date;
    // this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
    //   billingMonth: this.initialFilters.billingMonth,
    //   ...this.initialFilters,
    // })]);

  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      billingMonth: 'date',
      ofwca: 'string',
      partner: 'string',
      owner: 'string',
      in$status: 'int[]',
      gte$totalCollection: 'string',
      lte$totalCollection: 'string',
      gte$totalCommission: 'string',
      lte$totalCommission: 'string',
      assetsRegional: 'string',
      number: 'string',
      contractType: 'int'
    }, filters);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  loadListData(): void {
    this.commissionSettlementService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.initialFilters);
  }

  downloadReport(): void {
    this.commissionSettlementService.downloadReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }
  generateCommissionSettlements(commissionSettlement: CommissionSettlement): void {
    this.processingRequest = true;
    this.commissionSettlementService.generateCommissionSettlements(commissionSettlement.uuid)
        .subscribe((response) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {
              jobId: response.uuid,
              showProgress: false,
              title: `Generowanie rozliczenie dla: ${commissionSettlement?.partner?.shortName ? commissionSettlement?.partner?.shortName : commissionSettlement?.ofwca?.fullName}`
            },
          });
          dialogRef.afterClosed().subscribe(result => {
            this.loadListData();
            this.snack.open(`Rozliczenie dla: ${commissionSettlement?.partner?.shortName ? commissionSettlement?.partner?.shortName : commissionSettlement?.ofwca?.fullName} zostały zaktualizowane`, '', {duration: 2000});
          });
        }, (error: HttpErrorResponse) => {
          this.processingRequest = false;
          this.snack.open('Przy aktualizowaniu rozliczenia wystąpił błąd', error.status.toString(), {duration: 2000});
        });
  }

  protected readonly IMPORT_STATUS_IN_PROGRESS = IMPORT_STATUS_IN_PROGRESS;
}
